import type { NamedSignal } from './signalTemplate';

// The built-in host collectors of the cue-cli agent (zagentc/src/sysmetrics.h),
// as the setup screen offers them. `id` is the value in `spec.collectors`.
export interface SystemCollector {
  id: string;
  label: string;
  hint: string;
  defaultOn: boolean;
}

export const SYSTEM_COLLECTORS: SystemCollector[] = [
  { id: 'cpu', label: 'CPU', hint: 'Usage %, user/system/iowait/steal, per core', defaultOn: true },
  { id: 'mem', label: 'RAM & swap', hint: 'Total, available, used, used %', defaultOn: true },
  { id: 'load', label: 'Load average', hint: '1, 5 and 15 minute', defaultOn: true },
  { id: 'df', label: 'Disk free (df)', hint: 'Size, free, used %, inodes per mount', defaultOn: true },
  { id: 'iostat', label: 'Disk I/O (iostat)', hint: 'Read/write bytes and IOPS, utilisation, await (Linux)', defaultOn: true },
  { id: 'net', label: 'Network (ip -s link)', hint: 'Throughput, packets, errors, drops, link state (Linux)', defaultOn: true },
  { id: 'temp', label: 'Temperature', hint: 'Thermal zones and hwmon sensors (Linux)', defaultOn: true },
  { id: 'uptime', label: 'Uptime', hint: 'Seconds since boot', defaultOn: true },
  { id: 'procs', label: 'Processes & file handles', hint: 'Running/total, open files vs limit (Linux)', defaultOn: true },
  { id: 'du', label: 'Directory size (du)', hint: 'Disk usage of the paths you list - a bounded walk', defaultOn: false },
];

export interface SystemSetup {
  channel: string;
  collectors: string[];
  intervalSec: number;
  duPaths?: string[];
  dfMounts?: string[];
  scripts?: CustomScript[];
}

// A user-written shell command the agent runs on its own machine (opt-in there
// via ZAGENTC_ALLOW_SCRIPTS=1). Its stdout lines are `<metric> <number> [k=v ...]`
// or a bare number, which is named after the script.
export interface CustomScript {
  name: string;
  command: string;
  timeoutSec?: number;
}

export const SCRIPT_NAME = /^[A-Za-z0-9_:.]+$/;
export const validScripts = (list: CustomScript[]): CustomScript[] => list.filter((x) => SCRIPT_NAME.test(x.name.trim()) && x.command.trim() !== '');

const lines = (s: string): string[] => s.split(/[\n,]/).map((x) => x.trim()).filter(Boolean);
export const parseList = lines;

// The exact Signal names each collector emits (zagentc/src/sysmetrics.c) when
// the mapping spec has no `transform`/`rules`/`outputs` - which is every
// fresh Host metrics integration this screen creates (buildSystemSpec never
// writes any of those three). With none, the agent sends each sample
// straight through as a Signal named exactly this - see mapping_import.c's
// route_collected: `shaped` (any of the three present) goes through the
// mapping engine, otherwise every sample is emit_sample'd under its own
// collector-given name. So a fresh integration's Signals never match any
// existing template by name unless a template happens to already use these
// exact names - the reason hostMetricSignals/buildHostMetricsTemplate exist:
// a template generated from this fixed catalog matches on the very first run,
// no examples or agent output needed to "discover" the names first.
//
// One entry per Signal name, not per label dimension - a metric with several
// instances (a df line per mount, an iostat/net line per device/interface, a
// temp reading per sensor, a du line per path) is still ONE Signal name
// carrying a different `mount`/`device`/`interface`/`sensor`/`path` label per
// instance, so one template entry already covers all of them.
export const HOST_METRIC_SIGNALS: Record<string, NamedSignal[]> = {
  cpu: [
    { name: 'cpu_usage_percent', emoji: '🖥️' },
    { name: 'cpu_user_percent', emoji: '🖥️' },
    { name: 'cpu_system_percent', emoji: '🖥️' },
    { name: 'cpu_iowait_percent', emoji: '🖥️' },
    { name: 'cpu_steal_percent', emoji: '🖥️' },
    { name: 'cpu_count', emoji: '🖥️' },
  ],
  load: [
    { name: 'load_1m', emoji: '📈' },
    { name: 'load_5m', emoji: '📈' },
    { name: 'load_15m', emoji: '📈' },
  ],
  mem: [
    { name: 'mem_total_bytes', emoji: '🧠' },
    { name: 'mem_used_bytes', emoji: '🧠' },
    { name: 'mem_available_bytes', emoji: '🧠' },
    { name: 'mem_used_percent', emoji: '🧠' },
    { name: 'swap_total_bytes', emoji: '🧠' },
    { name: 'swap_used_bytes', emoji: '🧠' },
    { name: 'swap_used_percent', emoji: '🧠' },
  ],
  df: [
    { name: 'disk_free_bytes', emoji: '💾' },
    { name: 'disk_total_bytes', emoji: '💾' },
    { name: 'disk_used_bytes', emoji: '💾' },
    { name: 'disk_used_percent', emoji: '💾' },
    { name: 'inodes_used_percent', emoji: '💾' },
  ],
  iostat: [
    { name: 'disk_read_bytes_per_sec', emoji: '🔧' },
    { name: 'disk_write_bytes_per_sec', emoji: '🔧' },
    { name: 'disk_read_iops', emoji: '🔧' },
    { name: 'disk_write_iops', emoji: '🔧' },
    { name: 'disk_util_percent', emoji: '🔧' },
    { name: 'disk_await_ms', emoji: '🔧' },
  ],
  net: [
    { name: 'net_rx_bytes_per_sec', emoji: '🌐' },
    { name: 'net_tx_bytes_per_sec', emoji: '🌐' },
    { name: 'net_rx_packets_per_sec', emoji: '🌐' },
    { name: 'net_tx_packets_per_sec', emoji: '🌐' },
    { name: 'net_rx_errors', emoji: '🌐' },
    { name: 'net_tx_errors', emoji: '🌐' },
    { name: 'net_rx_dropped', emoji: '🌐' },
    { name: 'net_tx_dropped', emoji: '🌐' },
    { name: 'net_link_up', emoji: '🌐' },
  ],
  temp: [{ name: 'temperature_celsius', emoji: '🌡️' }],
  uptime: [{ name: 'uptime_seconds', emoji: '⏱️' }],
  procs: [
    { name: 'procs_running', emoji: '⚙️' },
    { name: 'procs_total', emoji: '⚙️' },
    { name: 'filefd_allocated', emoji: '⚙️' },
    { name: 'filefd_max', emoji: '⚙️' },
    { name: 'filefd_used_percent', emoji: '⚙️' },
  ],
  du: [{ name: 'disk_usage_bytes', emoji: '📁' }],
};

// Every Signal name the picked collectors will actually produce, in catalog
// (not pick) order, deduped - what a matching template needs to cover.
export function hostMetricSignals(collectors: string[]): NamedSignal[] {
  const picked = new Set(collectors);
  const out: NamedSignal[] = [];
  const seen = new Set<string>();
  for (const c of SYSTEM_COLLECTORS) {
    if (!picked.has(c.id)) continue;
    for (const s of HOST_METRIC_SIGNALS[c.id] ?? []) {
      if (seen.has(s.name)) continue;
      seen.add(s.name);
      out.push(s);
    }
  }
  return out;
}

// The Mapping spec for a host-metrics integration: no target (the agent reads
// its own host), only what to collect and where the Signals go.
export function buildSystemSpec(s: SystemSetup): Record<string, unknown> {
  const spec: Record<string, unknown> = {
    channel: s.channel,
    intervalSec: Math.max(10, Math.round(s.intervalSec) || 60),
    collectors: s.collectors,
  };
  if (s.collectors.includes('du') && s.duPaths?.length) spec.duPaths = s.duPaths;
  if (s.dfMounts?.length) spec.dfMounts = s.dfMounts;
  const scripts = validScripts(s.scripts ?? []);
  if (scripts.length) spec.scripts = scripts.map((x) => ({ name: x.name.trim(), command: x.command, timeoutSec: Math.min(120, Math.max(1, Math.round(x.timeoutSec ?? 10))) }));
  return spec;
}
